import type { ErrorSchema } from '@/shared/tools/json-schema-utils'
import type { JSONSchemaType } from 'ajv'
import type { ChangeEvent } from 'react'

import React from 'react'
import { any, isNaN } from 'lodash/fp'
import { InfoIcon } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Switch } from '@/shared/components/ui/switch'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { numericOnly } from '@/shared/tools/constants'

import { InputGroup, InputGroupAddon, InputGroupInput } from '../ui/input-group'

type AnyJSONScheme = JSONSchemaType<any>
const transformValue = (value: string) => {
	try {
		if (any((i) => value == i, ['', '-', '-0', '-0.'])) return value
		const val = Number(value.replace(numericOnly, ''))

		return isNaN(val) ? value : val
	} catch {
		return value
	}
}
type RHFSettingsFieldProps = {
	value: any
	name: string
	onChange: (value: any) => void
	schema: JSONSchemaType<any>
	errorSchema?: ErrorSchema
}
function RHFSettingsField({
	value,
	name,
	onChange,
	schema,
	errorSchema,
}: RHFSettingsFieldProps) {
	const { t } = useTranslation()

	const defaultValue = schema?.default || 1
	const nullable = schema.type.includes('null')
	const bool = schema.type.includes('boolean')
	const checked = bool ? value : value !== null
	const type = schema.type.filter((t: string) => t != 'null')[0]
	const error = errorSchema?.message
	const translatedDescription = schema.description
		? t(schema.description, { defaultValue: schema.description })
		: ''
	const translatedTitle = t(
		schema.title || `${'og_stoplist_default_rules'}_${name}`,
		{ defaultValue: schema.title }
	)
	const handleChangeField = (e: ChangeEvent<HTMLInputElement>) => {
		onChange(transformValue(e.target.value))
	}

	return (
		<div className='flex flex-col' key={name}>
			<div className='flex h-8 items-center space-x-3'>
				{(nullable || bool) && (
					<Switch
						name={name}
						data-name={name}
						checked={checked}
						onCheckedChange={(checked) =>
							onChange(
								type === 'boolean' ? checked : checked ? defaultValue : null
							)
						}
					/>
				)}
				<div className='flex items-center space-x-3'>
					<label className='flex items-center'>{translatedTitle}</label>
					{!bool && value !== null && (
						<InputGroup>
							<InputGroupInput
								type={type}
								value={value}
								name={`${name}_input_value`}
								aria-invalid={(!value && value !== 0) || error}
								onChange={handleChangeField}
							/>
							<InputGroupAddon align='inline-end'>
								{schema.measure &&
									t(schema.measure, { defaultValue: schema.measure })}
							</InputGroupAddon>
						</InputGroup>
					)}
				</div>
				{schema.description && translatedDescription?.length > 100 && (
					<Tooltip>
						<TooltipTrigger asChild>
							<InfoIcon className='size-4' />
						</TooltipTrigger>
						<TooltipContent>{translatedDescription}</TooltipContent>
					</Tooltip>
				)}
			</div>
			{error && <p className='text-sm text-destructive'>{error}</p>}
			{schema.description && translatedDescription.length < 100 && (
				<p className='text-muted-foreground'>{translatedDescription}</p>
			)}
		</div>
	)
}
type RHFSettingsWidgetProps = {
	schema: AnyJSONScheme
	name?: string
	shouldUnregister?: boolean
}
export default function RHFSettingsWidget({
	schema,
	name,
	shouldUnregister,
}: RHFSettingsWidgetProps) {
	const { control } = useFormContext()
	const options = Object.entries(
		schema.properties as Record<string, AnyJSONScheme>
	)

	return (
		<div className='flex flex-col gap-2 text-sm'>
			{options.map(([fieldName, fieldSchema]: [string, AnyJSONScheme]) => {
				const defaultValue = fieldSchema?.default

				return (
					<Controller
						key={fieldName}
						name={name ? `${name}.${fieldName}` : fieldName}
						shouldUnregister={shouldUnregister}
						defaultValue={defaultValue}
						control={control}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<RHFSettingsField
								errorSchema={error}
								value={value}
								name={fieldName}
								schema={fieldSchema}
								onChange={onChange}
							/>
						)}
					/>
				)
			})}
		</div>
	)
}
